/**
 * Shared tracking v2 configuration: states, nav, stuck thresholds.
 */

import { prettyStep } from './trackingWords';

export const TRACKING_STATES = {
  good: {
    code: 'good',
    label: 'Good',
    icon: 'check-circle',
    cssVar: '--trk-good'
  },
  watch: {
    code: 'watch',
    label: 'Keep an eye',
    icon: 'eye',
    cssVar: '--trk-watch'
  },
  act: {
    code: 'act',
    label: 'Needs you',
    icon: 'exclamation-circle',
    cssVar: '--trk-act'
  }
};

/** Minutes without activity before a live journey counts as stuck (§8.2). */
export const STUCK_THRESHOLDS_MINUTES = {
  ENTRY: 3,
  DETAILS: 5,
  ASSESSMENT: 8,
  ACCOUNT: 4,
  PAYMENT: 3,
  BOOKING: 5,
  CONFIRMATION: 5,
  SUPPORT: 5
};

/** Single nav array for sidebar + in-page header (§4.1). */
export const TRACKING_NAV = [
  {
    label: 'Today',
    to: '/tracking/today',
    hint: 'How are we doing?'
  },
  {
    label: 'People',
    to: '/tracking/people',
    hint: 'Who is booking, and who\'s stuck?'
  },
  {
    label: 'Where they stop',
    to: '/tracking/where-they-stop',
    hint: 'Why don\'t people finish?'
  },
  {
    label: 'Times & slots',
    to: '/tracking/times',
    hint: 'When are people booking, and do we have room?'
  },
  {
    label: 'Fixes',
    to: '/tracking/fixes',
    hint: 'Did what we changed actually work?'
  }
];

const LIVE_STATUSES = new Set(['ACTIVE', 'IDLE']);

export function stuckThresholdMinutes(step) {
  return STUCK_THRESHOLDS_MINUTES[step] || 5;
}

export function minutesSince(iso, now = Date.now()) {
  if (!iso) return 0;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return 0;
  return Math.max(0, Math.floor((now - then) / 60000));
}

/**
 * Client-side stuck detection.
 * TODO(backend): a stuckSince field on the sessions endpoint would let us sort server-side.
 */
export function isJourneyStuck(session, now = Date.now()) {
  if (!session || !LIVE_STATUSES.has(session.status)) {
    return false;
  }
  const step = session.currentStep;
  const threshold = stuckThresholdMinutes(step);
  const idle = minutesSince(session.lastActivityAt, now);
  return idle >= threshold;
}

export function getStuckMinutes(session, now = Date.now()) {
  if (!session) return 0;
  return minutesSince(session.lastActivityAt, now);
}

export function stuckLabel(session, now = Date.now()) {
  const mins = getStuckMinutes(session, now);
  return mins > 0 ? `Paused ${mins} min` : 'Paused';
}

export function stuckSummaryLine(sessions = [], now = Date.now()) {
  const stuck = sessions.filter((s) => isJourneyStuck(s, now));
  if (!stuck.length) return '';

  const byStep = stuck.reduce((acc, s) => {
    const step = s.currentStep || 'UNKNOWN';
    acc[step] = (acc[step] || 0) + 1;
    return acc;
  }, {});

  const topStep = Object.keys(byStep).sort((a, b) => byStep[b] - byStep[a])[0];
  const stepLabel = prettyStep(topStep, { short: true });
  const onStep = stuck.filter((s) => s.currentStep === topStep).length;

  return `${stuck.length} are stuck. ${onStep} of them on ${stepLabel}.`;
}
